import apiClient, { API_PREFIX } from './client';
import { apiLogger } from '../utils/logger';
import type { Sticker } from './ems';
import type {
  Order,
  OrdersResponse,
  ShippingLabelResponse,
  BatchShippingLabelResponse,
} from '../types/order.types';

// Re-export centralized types for backward compatibility
export type {
  Order,
  OrdersResponse,
  ShippingLabelResponse,
  BatchShippingLabelResponse,
} from '../types/order.types';

// Legacy shipping label types (still using snake_case for API compatibility)
// TODO: Migrate these to centralized types in types/order.types.ts

export interface ShippingLabelPayload {
  // Always uses Thailand Post EMS as per PRP requirement
}

export async function fetchOrders(perPage = 20, page = 1, status?: string, hasPrinted?: boolean): Promise<Order[]> {
  try {
    const params: Record<string, any> = { perPage, page };
    if (status) {
      params.status = status;
    }
    if (hasPrinted !== undefined) {
      params.hasPrinted = hasPrinted ? 'true' : 'false';
    }

    const response = await apiClient.get<{ success: boolean; data: OrdersResponse }>(`${API_PREFIX}/orders`, {
      params,
    });
    // Backend wraps response in { success: true, data: { orders: [...], pagination: {...} } }
    return response.data.data.orders;
  } catch (error) {
    apiLogger.error('[Order API] fetchOrders error', error);
    throw error;
  }
}

// NOTE: Removed fetchOrdersDetailed to fix N+1 query problem
// Backend now returns complete order details (including items) in fetchOrders()
// This eliminates the need to fetch each order detail separately

export async function fetchOrderById(orderId: string): Promise<Order> {
  try {
    const response = await apiClient.get<{ success: boolean; data: Order }>(`${API_PREFIX}/orders/${orderId}`);
    if (!response.data?.success || !response.data.data) {
      throw new Error('Failed to fetch order');
    }
    return response.data.data;
  } catch (error) {
    apiLogger.error('[Order API] fetchOrderById error', error);
    throw error;
  }
}

export interface ShippingLabelPayload {
  // Removed courier_service and service_type options
  // Always uses Thailand Post EMS as per PRP requirement
  // PRP: "ไม่มีขนส่งหลายเจ้าไม่สามารถเลือกบริษัทขนส่งได้"
}

export type ShippingLabelSticker = Sticker & {
  stickerCode?: string | null;
  message?: string;
  assignedAt?: string;
};

// NEW: ShippingLabelInfo interface removed as it is replaced by ShippingLabel from centralized types


export async function printShippingLabel(orderId: string, payload: ShippingLabelPayload = {}): Promise<ShippingLabelResponse> {
  try {
    const response = await apiClient.post<{ success: boolean; data: ShippingLabelResponse }>(
      `${API_PREFIX}/orders/${orderId}/shipping-label`,
      payload
    );
    return response.data.data;
  } catch (error) {
    apiLogger.error('[Order API] printShippingLabel error', error);
    throw error;
  }
}

export async function batchPrintShippingLabels(orderIds: string[], payload: ShippingLabelPayload = {}): Promise<BatchShippingLabelResponse> {
  try {
    const response = await apiClient.post<{ success: boolean; data: BatchShippingLabelResponse }>(
      `${API_PREFIX}/orders/batch-shipping-labels`,
      {
        orderIds,
        ...payload
      }
    );
    return response.data.data;
  } catch (error) {
    apiLogger.error('[Order API] batchPrintShippingLabels error', error);
    throw error;
  }
}

export interface TodayOrderStats {
  totalOrders: number;
  totalRevenue: number;
}

export async function fetchTodayOrderStats(): Promise<TodayOrderStats> {
  try {
    const response = await apiClient.get<{ success: boolean; data: TodayOrderStats }>(`${API_PREFIX}/orders/admin/today-stats`);
    if (!response.data?.success || !response.data.data) {
      throw new Error('Failed to fetch today order stats');
    }
    return response.data.data;
  } catch (error) {
    apiLogger.error('[Order API] fetchTodayOrderStats error', error);
    throw error;
  }
}
